import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

export interface Owner {
  connection: {
    id: number
    display_name: string
    connection_type?: string
    status: 'active' | 'paused' | 'removed'
  } | null
  external_url?: string | null
  last_synced_at?: string | null
}

export function isConnectionOwned(owner?: Owner | string | null): owner is Owner {
  return Boolean(owner && typeof owner !== 'string' && owner.connection)
}

function relativeTime(value: string) {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(value).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

function connectionSystem(connectionType?: string) {
  if (!connectionType) return 'source'
  return connectionType === 'quickbooks_online'
    ? 'QuickBooks Online'
    : connectionType.charAt(0).toUpperCase() + connectionType.slice(1)
}

export function OwnedBanner({
  owner,
  externalUrl,
  lastSyncedAt,
}: {
  owner?: Owner | string | null
  externalUrl?: string | null
  lastSyncedAt?: string | null
}) {
  if (!isConnectionOwned(owner)) return null
  const ownedOwner = owner as Owner
  const connection = ownedOwner.connection!
  const removed = connection.status === 'removed'
  return (
    <div
      className={`flex items-center gap-3 rounded-md border px-3 py-2 text-body-sm ${removed ? 'border-border bg-muted/40' : 'border-primary/20 bg-primary/5'}`}
    >
      <span className="font-medium text-foreground">
        From {connection.display_name}
        {removed ? ' (removed)' : ''}
      </span>
      {(lastSyncedAt ?? ownedOwner.last_synced_at) && (
        <span className="text-muted-foreground">
          {' '}
          · Last synced {relativeTime(lastSyncedAt ?? ownedOwner.last_synced_at!)}
        </span>
      )}
      {connection.status === 'paused' && <Badge variant="warning">Sync paused</Badge>}
      {(externalUrl ?? ownedOwner.external_url) && !removed && (
        <Button asChild variant="outline" size="sm" className="ml-auto">
          <a
            href={externalUrl ?? ownedOwner.external_url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in {connectionSystem(connection.connection_type)} ↗
          </a>
        </Button>
      )}
    </div>
  )
}

export interface OwnedRecord {
  owner?: Owner | string | null
  external_url?: string | null
  last_synced_at?: string | null
}

export function OwnedChip({ owner }: { owner?: Owner | string | null }) {
  if (!isConnectionOwned(owner)) return null
  const ownedOwner = owner as Owner
  const removed = ownedOwner.connection!.status === 'removed'
  return (
    <Badge variant={removed ? 'neutral' : 'info'} className="whitespace-nowrap">
      From {ownedOwner.connection!.display_name}
      {removed ? ' (removed)' : ''}
    </Badge>
  )
}
